import type { Dispatch, SetStateAction } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link } from '@mui/material';
import { Link as RouterLink } from 'react-router';

import Button from '@/components/Buttons/Button';
import type { FeedInfo } from '@/pages/Markets/MarketsComponents/Feed/types';

import { FeedArticleDialogLink } from './FeedArticleDialogLink';

type FeedCtaProps = Pick<FeedInfo['values'], 'CTAValue' | 'CTAType' | 'CTALabel'> & {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
};

const CTA_COLOR = '#E84E0F';
const LINK_SX = { textDecoration: 'none' } as const;
const CTA_BUTTON_SX = {
  color: CTA_COLOR,
  p: 0,
  m: 0,
  minWidth: 0,
  border: 0,
  mt: '-3px'
} as const;
const CTA_LINK_CONFIG = {
  Catalog: { label: 'See in catalog', path: 'catalog' },
  Market: { label: 'Go to market', path: 'markets' }
} as const;

export const FeedCta = ({ CTAType, CTAValue, CTALabel, open, setOpen }: FeedCtaProps) => {
  if (!CTAType) {
    return null;
  }

  if (CTAType === 'Catalog' || CTAType === 'Market') {
    const { label, path } = CTA_LINK_CONFIG[CTAType];

    return (
      <Link
        color={CTA_COLOR}
        component={RouterLink}
        sx={LINK_SX}
        to={`../${path}/${CTAValue}`}
      >
        {label}
      </Link>
    );
  }

  if (CTAType === 'Symbol') {
    return (
      <Button
        sx={CTA_BUTTON_SX}
        variant='text'
        onClick={() => {
          setOpen(v => !v);
        }}
      >
        See codes{' '}
        <ExpandMoreIcon sx={{ m: 0, transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }} />
      </Button>
    );
  }

  if (CTAType === 'Article') {
    return (
      <FeedArticleDialogLink
        CTALabel={CTALabel}
        CTAValue={CTAValue}
      />
    );
  }

  if (CTAType === 'Link') {
    return (
      <Link
        color={CTA_COLOR}
        href={CTAValue}
        rel='noreferrer noopener'
        sx={LINK_SX}
        target='_blank'
      >
        {CTALabel ?? 'Read more'}
      </Link>
    );
  }

  return null;
};
